import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Video, 
  ShieldCheck, 
  Users, 
  MessageCircle,
  Globe,
  Heart,
  Sparkles,
  ArrowRight,
  PlayCircle
} from 'lucide-react';
import axios from 'axios';
import HowItWorksModal from '@/components/HowItWorksModal';
import SEO from '@/components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Landing = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    users_online: 0,
    active_chats: 0,
    waiting_users: 0
  });
  const [showHowItWorks, setShowHowItWorks] = useState(false);

  useEffect(() => {
    fetchStats();
    const interval = setInterval(fetchStats, 5000);
    return () => clearInterval(interval);
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const startChat = async () => {
    try {
      const response = await axios.post(`${API}/session`, { interests: [] });
      const { session_id, anonymous_name } = response.data;
      
      localStorage.setItem('toonah_session', JSON.stringify({
        session_id,
        anonymous_name
      }));
      
      navigate('/chat');
    } catch (error) {
      console.error('Error creating session:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-blue-50 to-cyan-50 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
      <SEO
        title="Toonah — Meet Someone New. Instantly."
        description="Anonymous one-on-one video conversations with people around the world. No registration. No hassle. Just real human connections."
        path="/"
      />
      <div className="noise-overlay" />
      
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 glass-card">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex items-center space-x-2 cursor-pointer"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          >
            <img
              src="/toonah-wordmark.webp"
              alt="Toonah"
              data-testid="header-logo"
              className="h-8 sm:h-9 w-auto object-contain select-none"
              draggable={false}
            />
          </motion.div>
          
          <div className="hidden md:flex items-center space-x-8">
            <a href="#home" className="text-sm font-semibold hover:text-blue-600 transition-colors">Home</a>
            <button 
              onClick={() => setShowHowItWorks(true)}
              className="text-sm font-semibold hover:text-blue-600 transition-colors"
            >
              How it Works
            </button>
            <a href="#safety" className="text-sm font-semibold hover:text-blue-600 transition-colors">Safety</a>
            <button 
              onClick={() => navigate('/about')}
              className="text-sm font-semibold hover:text-blue-600 transition-colors"
            >
              About
            </button>
            <button 
              onClick={() => navigate('/privacy')}
              className="text-sm font-semibold hover:text-blue-600 transition-colors"
            >
              Privacy
            </button>
            <button 
              onClick={() => navigate('/contact')}
              className="text-sm font-semibold hover:text-blue-600 transition-colors"
            >
              Contact
            </button>
          </div>
        </div>
      </nav>

      {/* How It Works Modal */}
      <HowItWorksModal 
        isOpen={showHowItWorks} 
        onClose={() => setShowHowItWorks(false)} 
      />

      {/* Hero Section */}
      <section id="home" className="pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center space-x-2 px-4 py-2 rounded-full bg-blue-100 dark:bg-blue-900/30 mb-6">
                <Sparkles className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-bold text-blue-600">ANONYMOUS & INSTANT</span>
              </div>
              
              <h1 className="text-5xl sm:text-6xl font-black tracking-tighter leading-none mb-6">
                Meet Someone New.
                <span className="gradient-text block mt-2">Instantly.</span>
              </h1>
              
              <p className="text-base leading-relaxed text-slate-700 dark:text-slate-300 mb-8 max-w-xl">
                Anonymous one-on-one video conversations with people around the world. 
                No registration. No hassle. Just real human connections.
              </p>
              
              <div className="flex flex-wrap gap-4">
                <button
                  onClick={startChat}
                  data-testid="start-chat-button"
                  className="group px-8 py-4 bg-blue-600 hover:bg-blue-700 text-white rounded-full font-bold text-lg flex items-center space-x-2 shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5"
                >
                  <Video className="w-5 h-5" />
                  <span>Start Video Chat</span>
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
                
                <button
                  onClick={() => setShowHowItWorks(true)}
                  data-testid="how-it-works-button"
                  className="px-8 py-4 bg-white dark:bg-slate-800 border-2 border-slate-200 dark:border-slate-700 rounded-full font-bold text-lg flex items-center space-x-2 hover:border-blue-600 transition-all hover:-translate-y-0.5"
                >
                  <PlayCircle className="w-5 h-5" />
                  <span>How it Works</span>
                </button>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1758874384555-de68b8035c24?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwzfHxoYXBweSUyMGRpdmVyc2UlMjB5b3VuZyUyMHBlb3BsZSUyMHZpZGVvJTIwY2FsbHxlbnwwfHx8fDE3ODI2NDQ3NzB8MA&ixlib=rb-4.1.0&q=85"
                  alt="Happy person video chatting"
                  className="w-full rounded-2xl"
                />
                <div className="absolute bottom-6 right-6 w-48 h-36 rounded-xl overflow-hidden border-4 border-white shadow-xl">
                  <img 
                    src="https://images.unsplash.com/photo-1758598497528-d8d9b3f22894?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwyfHxoYXBweSUyMGRpdmVyc2UlMjB5b3VuZyUyMHBlb3BsZSUyMHZpZGVvJTIwY2FsbHxlbnwwfHx8fDE3ODI2NDQ3NzB8MA&ixlib=rb-4.1.0&q=85"
                    alt="Local video preview"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Live Stats */}
      <section className="py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-3 gap-6"
          >
            <div className="glass-card p-8 rounded-2xl">
              <Users className="w-10 h-10 text-blue-600 mb-4" />
              <div className="text-4xl font-black mb-2">{stats.users_online.toLocaleString()}</div>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Users Online</div>
            </div>
            
            <div className="glass-card p-8 rounded-2xl md:col-span-1">
              <MessageCircle className="w-10 h-10 text-cyan-600 mb-4" />
              <div className="text-4xl font-black mb-2">{stats.active_chats.toLocaleString()}</div>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Active Chats</div>
            </div>
            
            <div className="glass-card p-8 rounded-2xl">
              <Globe className="w-10 h-10 text-green-600 mb-4" />
              <div className="text-4xl font-black mb-2">{stats.waiting_users.toLocaleString()}</div>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Waiting Users</div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Safety Section */}
      <section id="safety" className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <ShieldCheck className="w-16 h-16 text-green-600 mx-auto mb-6" />
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4">
              Your Safety is Our Priority
            </h2>
            <p className="text-base leading-relaxed text-slate-700 dark:text-slate-300 max-w-2xl mx-auto">
              Built with safety and respectful conversations in mind.
            </p>
          </motion.div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Users, title: 'Anonymous', desc: 'No personal information required' },
              { icon: ShieldCheck, title: 'Privacy Controls', desc: 'Reveal yourself only when you choose' },
              { icon: Heart, title: 'Report Abuse', desc: 'Easy reporting system' },
              { icon: Globe, title: 'Global Community', desc: 'Connect with respect' }
            ].map((feature, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="glass-card p-6 rounded-2xl"
              >
                <feature.icon className="w-8 h-8 text-blue-600 mb-4" />
                <h3 className="text-xl sm:text-2xl font-semibold tracking-tight mb-2">{feature.title}</h3>
                <p className="text-sm text-slate-600 dark:text-slate-400">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* About Teaser */}
      <section id="about-teaser" className="py-20 px-6">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass-card p-8 sm:p-12 rounded-3xl text-center"
            data-testid="about-teaser"
          >
            <p className="text-xs font-bold uppercase tracking-[0.24em] text-blue-600 mb-4">
              About Toonah
            </p>
            <p className="text-base sm:text-lg leading-relaxed text-slate-700 dark:text-slate-300 mb-4">
              Most online spaces ask us to present ourselves — what to share, how to look, and how we want others to see us.
            </p>
            <p className="text-base sm:text-lg leading-relaxed text-slate-700 dark:text-slate-300 mb-6">
              Toonah was created for something simpler: spontaneous, real conversations with one person at a time.
            </p>
            <p className="text-lg sm:text-xl font-semibold text-slate-900 dark:text-white mb-8">
              One person. One conversation. No audience.
            </p>
            <button
              onClick={() => navigate('/about')}
              data-testid="read-our-story-cta"
              className="group inline-flex items-center space-x-2 text-sm font-semibold text-blue-600 hover:text-blue-700 transition-colors"
            >
              <span>Read our story</span>
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 px-6">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4">Frequently Asked Questions</h2>
          </motion.div>
          
          <div className="space-y-4">
            {[
              { q: 'Do I need to register?', a: 'No registration is required. Just start chatting without creating an account.' },
              { q: 'Is it safe?', a: 'Toonah is designed for anonymous, respectful conversation. You control when to reveal yourself, and every user has a one-tap Report button that our team reviews.' },
              { q: 'What if I see inappropriate content?', a: 'Use the Report button immediately. Reports are reviewed and appropriate action may be taken.' },
              { q: 'Can I choose who I talk to?', a: 'Toonah currently matches you randomly. You can choose when to reveal yourself and move to the next person anytime.' }
            ].map((faq, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="glass-card p-6 rounded-2xl"
              >
                <h3 className="font-bold mb-2">{faq.q}</h3>
                <p className="text-sm text-slate-600 dark:text-slate-400">{faq.a}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-16 px-6 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            {/* Brand */}
            <div>
              <div className="flex items-center mb-3">
                <img
                  src="/toonah-wordmark.webp"
                  alt="Toonah"
                  data-testid="footer-logo"
                  className="h-7 w-auto object-contain select-none"
                  draggable={false}
                />
              </div>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Meet someone new instantly.
              </p>
            </div>

            {/* Links */}
            <div className="md:col-span-2">
              <div className="flex flex-wrap gap-6">
                <button 
                  onClick={() => navigate('/privacy')}
                  className="text-sm hover:text-blue-600 transition-colors"
                >
                  Privacy Policy
                </button>
                <button 
                  onClick={() => navigate('/terms')}
                  className="text-sm hover:text-blue-600 transition-colors"
                >
                  Terms
                </button>
                <a href="#safety" className="text-sm hover:text-blue-600 transition-colors">
                  Safety
                </a>
                <button 
                  onClick={() => navigate('/about')}
                  className="text-sm hover:text-blue-600 transition-colors"
                >
                  About
                </button>
                <button 
                  onClick={() => navigate('/contact')}
                  className="text-sm hover:text-blue-600 transition-colors"
                >
                  Contact
                </button>
              </div>
            </div>
          </div>

          {/* Copyright */}
          <div className="border-t border-slate-200 dark:border-slate-800 pt-6 text-center">
            <p className="text-sm text-slate-500">
              © 2026 Toonah. All Rights Reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;